import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiAlertCircle, FiArrowLeft, FiCheckCircle, FiMessageSquare, FiTrendingUp } from 'react-icons/fi';
import { apiService } from '../../services/api';
import './EvaluationResults.css';
import { useLanguage } from '../../context/LanguageContext';
import { translateEvaluationText } from '../../utils/evaluationText';

const SECTION_CONFIG = [
  { key: 'history', label: 'History taking', max: 30, color: '#2574d9' },
  { key: 'diagnosis', label: 'Diagnosis', max: 40, color: '#7657d6' },
  { key: 'management', label: 'Management plan', max: 30, color: '#168767' }
];

function asTextList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => typeof item === 'string' ? item : JSON.stringify(item));
  if (value == null || value === '') return [];
  return [typeof value === 'string' ? value : JSON.stringify(value)];
}

function roleLabel(role, t) {
  if (role === 'user') return t('youStudent');
  if (role === 'patient') return t('patientProfile');
  return t('clinicalSystem');
}

export default function EvaluationResults() {
  const { language, t } = useLanguage();
  const { submissionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(location.state?.submission || null);
  const [loading, setLoading] = useState(!location.state?.submission);
  const [error, setError] = useState('');

  useEffect(() => {
    if (submission) return;
    apiService.getMySubmissions()
      .then((items) => {
        const found = (Array.isArray(items) ? items : []).find((item) => String(item.id) === String(submissionId));
        if (!found) throw new Error('This evaluation could not be found.');
        setSubmission(found);
      })
      .catch((err) => setError(err.message || 'Could not load this evaluation.'))
      .finally(() => setLoading(false));
  }, [submission, submissionId]);

  const evaluation = useMemo(() => submission?.system_result || {}, [submission]);
  const breakdown = useMemo(() => (
    evaluation.breakdown && typeof evaluation.breakdown === 'object' ? evaluation.breakdown : {}
  ), [evaluation]);
  const transcript = Array.isArray(submission?.transcript) ? submission.transcript : [];
  const totalScore = Number(evaluation.total_score ?? evaluation.score ?? submission?.system_score) || 0;
  const improvementPlan = useMemo(() => {
    const explicit = asTextList(evaluation.improvement_plan);
    if (explicit.length) return explicit;
    return SECTION_CONFIG.flatMap(({ key }) => asTextList(breakdown[key]?.improvements));
  }, [evaluation.improvement_plan, breakdown]);

  if (loading) return <div className="results-state">{t('evaluationLoading')}</div>;
  if (error || !submission) {
    return <div className="results-state results-error"><p>{error || t('evaluationNotFound')}</p><button onClick={() => navigate('/dashboard')}>{t('backToCases')}</button></div>;
  }

  return (
    <main className="results-page">
      <div className="results-shell">
        <header className="results-topbar">
          <button type="button" className="results-back" onClick={() => navigate('/dashboard')}>
            <FiArrowLeft /> {t('backToCases')}
          </button>
          <span className="results-case">{submission.patient_name || submission.case_id}</span>
        </header>

        <section className="results-hero">
          <div>
            <span className="results-eyebrow">{t('encounterCompleted')}</span>
            <h1>{t('clinicalEvaluation')}</h1>
            <p>{translateEvaluationText(evaluation.feedback, language) || t('defaultFeedback')}</p>
          </div>
          <div className="results-total" aria-label={`Overall score ${totalScore} out of 100`}>
            <strong>{totalScore}</strong><span>/100</span><small>{t('overallScore')}</small>
          </div>
        </section>

        <section className="results-section-grid" aria-label="Evaluation breakdown">
          {SECTION_CONFIG.map(({ key, max, color }) => {
            const label = { history: t('historyTaking'), diagnosis: t('diagnosis'), management: t('management') }[key];
            const section = breakdown[key] || {};
            const score = Math.max(0, Math.min(max, Number(section.score) || 0));
            const achieved = asTextList(section.achieved);
            const partial = asTextList(section.partial);
            const missed = asTextList(section.missed);
            const reasons = asTextList(section.lost_marks_reason);
            const improvements = asTextList(section.improvements);
            const deductionDetails = reasons.length ? reasons : missed;
            return (
              <article className="results-section-card" key={key}>
                <div className="results-section-heading">
                  <div><span className="results-section-dot" style={{ background: color }} /><h2>{label}</h2></div>
                  <strong style={{ color }}>{score}/{max}</strong>
                </div>
                <div className="results-progress"><span style={{ width: `${(score / max) * 100}%`, background: color }} /></div>

                <div className="results-feedback-block positive">
                  <h3><FiCheckCircle /> {t('didWell')}</h3>
                  {achieved.length ? <ul>{achieved.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</ul> : <p>{t('noStrengths')}</p>}
                </div>
                {partial.length > 0 && (
                  <div className="results-feedback-block partial">
                    <h3><FiTrendingUp /> {t('partialCredit')}</h3>
                    <ul>{partial.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</ul>
                  </div>
                )}
                <div className="results-feedback-block deduction">
                  <h3><FiAlertCircle /> {t('marksLost')}</h3>
                  {deductionDetails.length ? <ul>{deductionDetails.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</ul> : <p>{t('noDeductions')}</p>}
                </div>
                <div className="results-feedback-block improve">
                  <h3><FiTrendingUp /> {t('howImprove')}</h3>
                  {improvements.length ? <ul>{improvements.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</ul> : <p>{t('keepApproach')}</p>}
                </div>
              </article>
            );
          })}
        </section>

        <section className="results-card results-next-steps">
          <h2><FiTrendingUp /> {t('nextPriorities')}</h2>
          {improvementPlan.length ? <ol>{improvementPlan.map((item, index) => <li key={index}>{translateEvaluationText(item, language)}</li>)}</ol> : <p>{t('reviewGuidance')}</p>}
        </section>

        <section className="results-answer-grid">
          <article className="results-card"><span className="results-card-label">{t('yourDiagnosis')}</span><p>{submission.student_diagnosis || '—'}</p></article>
          <article className="results-card"><span className="results-card-label">{t('managementPlan')}</span><p>{submission.student_plan || '—'}</p></article>
        </section>

        <section className="results-card results-transcript">
          <div className="results-transcript-title"><div><FiMessageSquare /><h2>{t('fullTranscript')}</h2></div><span>{transcript.length} {t('messages')}</span></div>
          {transcript.length ? (
            <div className="results-messages">
              {transcript.map((message, index) => (
                <div className={`results-message results-message-${message.role}`} key={`${message.role}-${index}`}>
                  <span>{roleLabel(message.role, t)}</span>
                  <p>{message.content}</p>
                </div>
              ))}
            </div>
          ) : <p className="results-empty">{t('noTranscript')}</p>}
        </section>

        <div className="results-actions">
          <button type="button" className="results-primary" onClick={() => navigate('/dashboard')}>{t('chooseAnother')}</button>
          <button type="button" className="results-secondary" onClick={() => window.print()}>{t('printEvaluation')}</button>
        </div>
      </div>
    </main>
  );
}
